import {useContext} from 'react';
import StatusIcon, { StatusIconModifier } from 'components/statusIcon';
import Tooltip, {TooltipAlignEnum, TooltipCursorEnum, TooltipModifier} from 'components/tooltip';
import {useTranslation} from 'react-i18next';
import {StatusEnum} from 'models/general';
import {StoreContext} from '../../models/import';
import { ImportTableBalanceStatus } from 'components/table/importTable';
import IconList, { IconListModifier } from 'components/iconList';

export enum BalanceStatusEnum {
  NoLiveDataAvailable = 0,
  MissingLiveData = 1,
  MissingImportedData = 2,
  MatchingLiveData = 3,
  MismatchingLiveData = 4,
  CoinIsNft = 5,
  SmallMismatchingLiveData = 6,
  UnexpectedNegativeValues = 7,
  IgnoreUserCurrencyMismatches = 8,
  EmptyCoinTicker = 9,
}

interface BalanceStatusTooltipProps {
  status: ImportTableBalanceStatus[];
  isDetail: boolean;
  cointainsLiveData: boolean;
}

export const getStatus = (items: ImportTableBalanceStatus[], isDetail: boolean, cointainsLiveData: boolean): StatusEnum => {
  var statusValue: StatusEnum;
  if (items.some((e: ImportTableBalanceStatus) => e.count > 0
  && e.balanceStatus === BalanceStatusEnum.UnexpectedNegativeValues)) {
    statusValue = StatusEnum.WARNING;
  } else if (items.some((e: ImportTableBalanceStatus) => e.count > 0
  && e.balanceStatus === BalanceStatusEnum.MismatchingLiveData)) {
    statusValue = StatusEnum.WARNING;
  } else if (items.some((e: ImportTableBalanceStatus) => e.count > 0
  && e.balanceStatus === BalanceStatusEnum.MissingLiveData)) {
    statusValue = StatusEnum.WARNING;
  } else if (items.some((e: ImportTableBalanceStatus) => e.count > 0
  && e.balanceStatus === BalanceStatusEnum.MissingImportedData)) {
    statusValue = StatusEnum.WARNING;
  }  else if (items.some((e: ImportTableBalanceStatus) => e.count > 0
  && e.balanceStatus === BalanceStatusEnum.EmptyCoinTicker)) {
    statusValue = StatusEnum.WARNING;
  } else if (items.some((e: ImportTableBalanceStatus) => e.count > 0
  && e.balanceStatus === BalanceStatusEnum.CoinIsNft && isDetail)) {
    statusValue = StatusEnum.GRAY_OUTLINE_SUCCESS;
  } else if (items.some((e: ImportTableBalanceStatus) => e.count > 0
  && e.balanceStatus === BalanceStatusEnum.NoLiveDataAvailable)) {
    statusValue = StatusEnum.SUCCESS;
  } else {
    statusValue = StatusEnum.SUCCESS;
  }

  if (!cointainsLiveData && statusValue === StatusEnum.SUCCESS) {
    statusValue = StatusEnum.GRAY_OUTLINE_SUCCESS;
  }

  return statusValue;  
};

export const getSmallMismatchValueInUserCurrency = () : string => {
  const { userSettings } = useContext(StoreContext);
  if (!userSettings?.currencyInUsdRate) return '';
  return (10 * userSettings.currencyInUsdRate).toFixed(0);
};

function BalanceStatusTooltip(props: BalanceStatusTooltipProps) {
  const { t } = useTranslation();
  const { userSettings } = useContext(StoreContext);
  const smallMismatchValueInUserCurrency : string = getSmallMismatchValueInUserCurrency();

  var countMismatches = 0

  var textValue: (string | null)[] = props.status.map((item: ImportTableBalanceStatus) => {
    if (item.count === 0) {
      return null;
    }
    let text: null | string = null;
    switch (item.balanceStatus) {
      case BalanceStatusEnum.NoLiveDataAvailable:
        return null
      case BalanceStatusEnum.MissingLiveData:
        if (props.cointainsLiveData) {
          countMismatches += item.count;
        }
        break;
      case BalanceStatusEnum.MissingImportedData:
        if (props.cointainsLiveData) {
          countMismatches += item.count;
        }
        break;
      case BalanceStatusEnum.MismatchingLiveData:
        if (props.cointainsLiveData) {
          countMismatches += item.count;
        }
        break;
      case BalanceStatusEnum.UnexpectedNegativeValues:
        if (item.count > 1) {
          text = `${item.count} ${t('Negative balances', { ns: 'overview' })}`;
        } else {
          text = `${item.count} ${t('Negative balance', { ns: 'overview' })}`;
        }
        if (props.isDetail) {
          text = t('Negative balance', { ns: 'overview' });
        }
        break;
      case BalanceStatusEnum.EmptyCoinTicker:
        text = `${item.count} ${t('Empty coin-ticker', { ns: 'overview' })}${item.count > 1 ? 's' : ''}`;
        break;
      case BalanceStatusEnum.CoinIsNft:
        if (props.isDetail) {
          text = t('No Live Balance for this NFT available', { ns: 'overview' });
        } else {
          return null;
        }
        break;
      case BalanceStatusEnum.SmallMismatchingLiveData:
         if (props.isDetail) {
          text = t(
            'Mismatching balance is below {{belowValue}} {{belowCurrency}} and above {{aboveValue}} {{aboveCurrency}}',
            { 
              belowValue: smallMismatchValueInUserCurrency,
              belowCurrency: userSettings.currency,
              aboveValue: `- ${smallMismatchValueInUserCurrency}`,
              aboveCurrency: userSettings.currency,
              ns: 'overview' 
            }
          );
        } else {
          return null;
        }
        break;
      case BalanceStatusEnum.IgnoreUserCurrencyMismatches:
         if (props.isDetail) {
          text = t(
            'Ignored {{userCurrency}} mismatch',
            { 
              userCurrency: userSettings.currency,
              ns: 'overview'
             }
          );
        } else {
          return null;
        }
        break;
      default:
        return null;
    }
    return text || null;
  });

  if (!props.cointainsLiveData) {
    textValue = [
      t('No live balance available', { ns: 'overview' }),
      ...textValue,
    ]
  }

  //custom logic for mismatches
  if (countMismatches > 0) {
    var balanceMismatchText = ''
    if (countMismatches > 1) {
      balanceMismatchText = `${countMismatches} ${t('Balance mismatches', { ns: 'overview' })}`;
    } else {
      balanceMismatchText = `${countMismatches} ${t('Balance mismatch', { ns: 'overview' })}`;
    }
    if (props.isDetail) {
      balanceMismatchText = t('Balance mismatch', { ns: 'overview' });
    }
    textValue = [
      ...textValue,
      balanceMismatchText
    ]
  }

  const title: string[] = textValue.filter((v) => v !== null);

  const statusValue = getStatus(props.status, props.isDetail, props.cointainsLiveData);

  return (
    <>
    {title.length > 0 ? (
            <Tooltip
              align={TooltipAlignEnum.RIGHT}
              cursor={TooltipCursorEnum.HELP}
              modifiers={[TooltipModifier.OVERVIEW]}
              tooltipChildren={(
                <IconList
                  items={title}
                  modifiers={[
                    IconListModifier.TABLE_SPACING, IconListModifier.OLD,
                  ]}
                />
              )}
            >
              <StatusIcon type={statusValue} modifiers={[StatusIconModifier.OVERVIEW]} />
            </Tooltip>
          ) : <StatusIcon type={statusValue} modifiers={[StatusIconModifier.OVERVIEW]} />
          }
    </>
  );
}

BalanceStatusTooltip.defaultProps = { ...{ } };

export default BalanceStatusTooltip;
